import type { ModeNames } from "@/modes/mode";
import { useCurrentScene } from "@/modes/mode-data-context";
import {
  CadModelObject,
  PanoObject,
  PointCloudObject,
  SheetObject,
  useCached3DObjectIfReady,
} from "@/object-cache";
import {
  isGaussianSplatsObject,
  isPanoObject,
  isPhotoObject,
  isPointCloudObject,
} from "@/object-cache-type-guard";
import { selectActiveCadModel } from "@/store/cad/cad-slice";
import { Features, selectHasFeature } from "@/store/features/features-slice";
import { selectModeName } from "@/store/mode-selectors";
import { useAppSelector, useAppStore } from "@/store/store-hooks";
import { IS_DEV } from "@/utils/env-constants";
import {
  selectIElement,
  useTypedEvent,
} from "@faro-lotv/app-component-toolbox";
import {
  FaroButton,
  FaroIconButton,
  FaroText,
  NO_TRANSLATE_CLASS,
  QuestionMarkInCircleIcon,
  TextField,
} from "@faro-lotv/flat-ui";
import { isIElementGenericImgSheet } from "@faro-lotv/ielement-types";
import {
  CadModel,
  LodCachingStrategyMaxChunks,
  OutOfCoreStreamCadModel,
  StreamCadModel,
  outOfCoreStreamCadModelSettings,
} from "@faro-lotv/lotv";
import { useApiClientContext } from "@faro-lotv/service-wires";
import {
  Checkbox,
  Divider,
  FormControlLabel,
  Stack,
  Tooltip,
} from "@mui/material";
import {
  useCallback,
  useEffect,
  useMemo,
  useReducer,
  useState,
  type JSX,
} from "react";
import { Color } from "three";
import { useFeatureDetectionContext } from "../common/feature-detection-context";
import { useThreeContext } from "../common/three-context/three-context";
import { useIElementsStats } from "./use-ielements-stats";

/** @returns a panel with dev functions enabled only using the dev server */
export function DevToolsPanel(): JSX.Element | null {
  const showDevTools =
    useAppSelector(selectHasFeature(Features.DevTools)) && IS_DEV;

  const activeMode = useAppSelector(selectModeName);
  const isInvalidMode = activeMode === "createArea" || activeMode === "start";

  if (!showDevTools || isInvalidMode) {
    return null;
  }

  return <DevToolsPanelImplementation activeMode={activeMode} />;
}

type DevToolsPanelImplementationProps = {
  activeMode: ModeNames;
};

function DevToolsPanelImplementation({
  activeMode,
}: DevToolsPanelImplementationProps): JSX.Element {
  const { main } = useCurrentScene();

  // If the mode is sheet, we do not acquire big models such as point clouds
  // or CAD models.
  const toAcquire = useMemo(() => {
    let result = main;
    if (activeMode === "sheet" && !isIElementGenericImgSheet(main)) {
      result = undefined;
    }
    return result;
  }, [activeMode, main]);
  const mainObject = useCached3DObjectIfReady(
    toAcquire,
    "DevToolsPanelImplementation",
  );

  const safeMainObject =
    mainObject &&
    !(mainObject instanceof Error) &&
    !isGaussianSplatsObject(mainObject) &&
    !isPhotoObject(mainObject)
      ? mainObject
      : undefined;

  const activeCad = useAppSelector(selectActiveCadModel);
  const cadModelObject = useCached3DObjectIfReady(
    activeMode === "sheet" ? undefined : activeCad,
    "DevToolsPanelImplementation",
  );

  const [isOpen, toggle] = useReducer((value) => !value, false);

  return (
    <Stack
      direction="column"
      component="fieldset"
      sx={{ alignContent: "center", justifyItems: "center", gap: 0.25 }}
    >
      <legend onClick={toggle}>
        Dev Panel <button>{isOpen ? "close" : "open"}</button>
      </legend>
      {isOpen && (
        <>
          <DepthButton mainObject={safeMainObject} />
          <Divider flexItem variant="middle" />
          <PcCacheEdit mainObject={safeMainObject} />
          <Divider flexItem variant="middle" />
          {cadModelObject && !(cadModelObject instanceof Error) && (
            <>
              <CadModelStatus cadModel={cadModelObject} />
              <Divider flexItem variant="middle" />
            </>
          )}
          <BackgroundSelector />
          <Divider flexItem variant="middle" />
          <HighlightTranslations />
          <Divider flexItem variant="middle" />
          <ShowPointerMovePreview />
          <Divider flexItem variant="middle" />
          <LogElement />
          <Divider flexItem variant="middle" />
          <IElementsStatsCheckBox />
          <Divider flexItem variant="middle" />
          <DebugTokenExpiration />
        </>
      )}
    </Stack>
  );
}

type ToolProps = {
  mainObject?: PanoObject | PointCloudObject | SheetObject;
};

function DepthButton({ mainObject }: ToolProps): JSX.Element {
  const [showDepthImage, toggle] = useReducer((val) => !val, false);

  const isEnabled = mainObject && isPanoObject(mainObject);

  useEffect(() => {
    if (mainObject && isPanoObject(mainObject)) {
      mainObject.showDepthImage = showDepthImage;
      return () => {
        mainObject.showDepthImage = false;
      };
    }
  }, [mainObject, showDepthImage]);

  return (
    <Stack
      direction="row"
      sx={{
        width: "100%",
        alignItems: "center",
        justifyContent: "space-between",
      }}
      onClick={isEnabled ? toggle : undefined}
    >
      <FaroText variant="bodyM">Show Depths</FaroText>
      <Checkbox disabled={!isEnabled} value={showDepthImage} />
    </Stack>
  );
}

function PcCacheEdit({ mainObject }: ToolProps): JSX.Element {
  const [pcNodeCacheSize, setPcNodeCacheSize] = useState(0);
  const [pcParallelFetches, setPcParallelFetches] = useState(4);
  const [nodesInCache, setNodesInCache] = useState(0);
  const [nodesInGpu, setNodesInGpu] = useState(0);

  useEffect(() => {
    if (!mainObject || !isPointCloudObject(mainObject)) return;
    const cacheSystem = mainObject.cacheCleanComputer;
    if (cacheSystem instanceof LodCachingStrategyMaxChunks) {
      setPcNodeCacheSize(cacheSystem.maxChunks);
    }
    setPcParallelFetches(mainObject.lodTreeFetcher.maxNodesToDownloadAtOnce);
    // eslint-disable-next-line func-style -- FIXME
    const updated = (): void => {
      setNodesInCache(mainObject.nodesInMemoryCount);
      setNodesInGpu(mainObject.nodesInGPU.size);
    };
    const handle = setInterval(updated, 1000);
    return () => {
      clearInterval(handle);
    };
  }, [mainObject]);

  useEffect(() => {
    if (mainObject && isPointCloudObject(mainObject)) {
      const cacheSystem = mainObject.cacheCleanComputer;
      if (cacheSystem instanceof LodCachingStrategyMaxChunks) {
        const oldSize = cacheSystem.maxChunks;
        cacheSystem.maxChunks = pcNodeCacheSize;
        return () => {
          cacheSystem.maxChunks = oldSize;
        };
      }
    }
  }, [mainObject, pcNodeCacheSize]);

  useEffect(() => {
    if (mainObject && isPointCloudObject(mainObject)) {
      const oldPar = mainObject.lodTreeFetcher.maxNodesToDownloadAtOnce;
      mainObject.lodTreeFetcher.maxNodesToDownloadAtOnce = pcParallelFetches;
      return () => {
        mainObject.lodTreeFetcher.maxNodesToDownloadAtOnce = oldPar;
      };
    }
  }, [mainObject, pcParallelFetches]);

  return (
    <>
      <Stack
        direction="row"
        sx={{
          width: "100%",
          alignItems: "center",
          justifyContent: "space-between",
        }}
      >
        <FaroText variant="bodyM">Pc Cached Nodes</FaroText>
        <TextField
          text={pcNodeCacheSize.toString()}
          onTextChanged={(value) => setPcNodeCacheSize(parseInt(value, 10))}
        />
      </Stack>
      <Stack
        direction="row"
        sx={{
          width: "100%",
          alignItems: "center",
          justifyContent: "space-between",
        }}
      >
        <FaroText variant="bodyM">Parallel Fetches</FaroText>
        <TextField
          text={pcParallelFetches.toString()}
          onTextChanged={(value) => setPcParallelFetches(parseInt(value, 10))}
        />
      </Stack>
      <FaroText variant="bodyM">Nodes in cache {nodesInCache}</FaroText>
      <FaroText variant="bodyM">Nodes in gpu {nodesInGpu}</FaroText>
    </>
  );
}

function BackgroundSelector(): JSX.Element {
  const { scene } = useThreeContext();
  const [color, setColor] = useState(scene?.background);

  useEffect(() => {
    if (!color || !scene) return;
    scene.background = color;
  }, [color, scene]);

  return (
    <Stack
      direction="row"
      sx={{
        width: "100%",
        alignItems: "center",
        justifyContent: "space-between",
      }}
    >
      <FaroText variant="bodyM">Background</FaroText>
      <input
        type="color"
        onChange={(e) => setColor(new Color(e.target.value))}
      />
    </Stack>
  );
}

// Using a global style node to highlight translations also when the dev tools unmount
let highlightTranslationsStyleNode: HTMLStyleElement | undefined;

/** @returns a toggle to highlight all Localize classes and vars */
function HighlightTranslations(): JSX.Element {
  const [isEnabled, setIsEnabled] = useState(!!highlightTranslationsStyleNode);

  const style = "{background: #0f0 !important;}";

  // Adds a background to the blacklisted elements and to their children
  const styleRule = `
    .${NO_TRANSLATE_CLASS}, var ${style}
    .${NO_TRANSLATE_CLASS} > * ${style}
    var > * ${style}`;

  function toggle(): void {
    if (highlightTranslationsStyleNode) {
      highlightTranslationsStyleNode.remove();
      highlightTranslationsStyleNode = undefined;
    } else {
      highlightTranslationsStyleNode = document.createElement("style");
      highlightTranslationsStyleNode.innerHTML = styleRule;
      document.head.appendChild(highlightTranslationsStyleNode);
    }
    setIsEnabled(!!highlightTranslationsStyleNode);
  }

  return (
    <FormControlLabel
      labelPlacement="start"
      label={
        <Stack direction="row" sx={{ alignItems: "center" }}>
          <FaroText variant="bodyM">Highlight Localize classes/vars </FaroText>
          <Tooltip
            title={
              <FaroText variant="bodyM" dark>
                Adds a background to all classes and elements that are excluded
                from Localize.js.
              </FaroText>
            }
          >
            <FaroIconButton size="xs">
              <QuestionMarkInCircleIcon />
            </FaroIconButton>
          </Tooltip>
        </Stack>
      }
      control={<Checkbox checked={isEnabled} onChange={toggle} />}
      sx={{
        ml: 0,
        ".MuiFormControlLabel-label": { width: "100%" },
      }}
    />
  );
}

type CadModelStatusProps = {
  /** The active CAD model */
  cadModel: CadModelObject;
};

/** @returns Component display the status of the active CAD model */
function CadModelStatus({ cadModel }: CadModelStatusProps): JSX.Element {
  const [completed, setCompleted] = useState(false);
  const [chunkState, setChunkState] = useState("");
  const [bufferState, setBufferState] = useState("");
  const [streaming, setStreaming] = useState(false);
  const [showAllChunkBoxes, setShowAllChunkBoxes] = useState(false);
  const [stopStreaming, setStopStreaming] = useState(false);
  const [cadVisibilityDistance, setCadVisibilityDistance] = useState("");
  const [cadMinVisibilityDistance, setCadMinVisibilityDistance] = useState("");
  const [filterByApparentSize, setFilterByApparentSize] = useState(
    outOfCoreStreamCadModelSettings.apparentSizeThreshold !== undefined,
  );

  const updateVisibilityDistance = useCallback(() => {
    if (cadModel instanceof OutOfCoreStreamCadModel) {
      setCadMinVisibilityDistance(
        `Min Visibility Distance: ${outOfCoreStreamCadModelSettings.minVisibleDistance?.toFixed(1) ?? "undefined"} m`,
      );
      setCadVisibilityDistance(
        `Visibility up to ${cadModel.maxVisibleChunkCameraDistance.toFixed(1)} m`,
      );
    }
  }, [cadModel]);

  useEffect(() => {
    if (cadModel instanceof StreamCadModel) {
      setCompleted(cadModel.isCompleted);
      setChunkState(cadModel.stats.chunks);
      setBufferState(cadModel.stats.bufferSize);
    }
    if (cadModel instanceof OutOfCoreStreamCadModel) {
      setStreaming(cadModel.isStreaming);
      updateVisibilityDistance();
    }
  }, [cadModel, updateVisibilityDistance]);

  useTypedEvent<void>(
    cadModel instanceof StreamCadModel ? cadModel.chunkLoaded : undefined,
    () => {
      if (!(cadModel instanceof StreamCadModel)) return;
      setChunkState(cadModel.stats.chunks);
      setBufferState(cadModel.stats.bufferSize);
    },
  );
  useTypedEvent<void>(
    cadModel instanceof StreamCadModel ? cadModel.streamCompleted : undefined,
    () => {
      if (!(cadModel instanceof StreamCadModel)) return;
      setCompleted(cadModel.isCompleted);
    },
  );

  const updateStreamCadModel2State = useCallback(() => {
    if (!(cadModel instanceof OutOfCoreStreamCadModel)) return;
    const toBeLoaded =
      cadModel.chunksInFrustum - cadModel.visibleChunksInFrustum;
    setChunkState(
      `Total ${cadModel.totalChunks} chunks: ${cadModel.chunksInMemory} inMemory, ${cadModel.chunksInFrustum} inFrustum, ${toBeLoaded} to load`,
    );
    const size = (cadModel.bytesLoaded / 1024 / 1024).toFixed();
    const maxSize = (cadModel.maxBufferBytes / 1024 / 1024).toFixed();
    setBufferState(`Memory ${size}/${maxSize} MB`);
    updateVisibilityDistance();
  }, [cadModel, updateVisibilityDistance]);

  useTypedEvent<void>(
    cadModel instanceof OutOfCoreStreamCadModel
      ? cadModel.chunkLoaded
      : undefined,
    updateStreamCadModel2State,
  );
  useTypedEvent<void>(
    cadModel instanceof OutOfCoreStreamCadModel
      ? cadModel.chunkDisposed
      : undefined,
    updateStreamCadModel2State,
  );
  useTypedEvent<void>(
    cadModel instanceof OutOfCoreStreamCadModel
      ? cadModel.frustumChanged
      : undefined,
    updateStreamCadModel2State,
  );

  useTypedEvent<void>(
    cadModel instanceof OutOfCoreStreamCadModel
      ? cadModel.streamingStarted
      : undefined,
    () => {
      if (cadModel instanceof OutOfCoreStreamCadModel) {
        setStreaming(cadModel.isStreaming);
      }
    },
  );
  useTypedEvent<void>(
    cadModel instanceof OutOfCoreStreamCadModel
      ? cadModel.streamingStopped
      : undefined,
    () => {
      if (cadModel instanceof OutOfCoreStreamCadModel) {
        setStreaming(cadModel.isStreaming);
      }
    },
  );
  return (
    <>
      {cadModel instanceof CadModel && (
        <FaroText variant="bodyM">
          CadModel Nodes {cadModel.nodesCount()}
        </FaroText>
      )}
      {cadModel instanceof StreamCadModel && (
        <>
          <FaroText variant="bodyM">
            CadModel Stream {completed ? " completed" : " in progress"}
          </FaroText>
          <FaroText variant="bodyM">CadModel Chunks {chunkState} </FaroText>
          <FaroText variant="bodyM">
            CadModel BufferSize {bufferState}{" "}
          </FaroText>
        </>
      )}
      {cadModel instanceof OutOfCoreStreamCadModel && (
        <Stack>
          <FaroText variant="bodyM">
            CAD Streaming {streaming ? "🟢" : "🔴"}
          </FaroText>
          <FaroText variant="bodyS">CAD {bufferState}</FaroText>
          <FaroText variant="bodyS">{chunkState} </FaroText>
          <FaroText variant="bodyS">{cadVisibilityDistance} </FaroText>
          <FaroText variant="bodyS">{cadMinVisibilityDistance} </FaroText>
          <Stack direction="row" sx={{ alignItems: "center" }}>
            <FaroText variant="bodyS">Show Chunk Boxes:</FaroText>
            <FaroText variant="bodyS">All</FaroText>
            <Checkbox
              checked={showAllChunkBoxes}
              onChange={(ev) => {
                setShowAllChunkBoxes(ev.target.checked);
                cadModel.showChunkBoxes = ev.target.checked;
              }}
            />
            <FaroText variant="bodyS">Stop streaming</FaroText>
            <Checkbox
              checked={stopStreaming}
              onChange={(ev) => {
                setStopStreaming(ev.target.checked);
                cadModel.stopStreaming = ev.target.checked;
              }}
            />
          </Stack>
          <Stack direction="row" sx={{ alignItems: "center", gap: 1 }}>
            <FaroText variant="bodyS">Filter by ApparentSize</FaroText>
            <Checkbox
              checked={filterByApparentSize}
              onChange={(ev) => {
                const newValue = ev.target.checked ? 0.01 : undefined;
                outOfCoreStreamCadModelSettings.apparentSizeThreshold =
                  newValue;
                setFilterByApparentSize(!!newValue);
              }}
            />
            <TextField
              text={
                outOfCoreStreamCadModelSettings.apparentSizeThreshold
                  ? outOfCoreStreamCadModelSettings.apparentSizeThreshold.toString()
                  : "NA"
              }
              disabled={!filterByApparentSize}
              onTextChanged={(text) => {
                const newValue = text ? Number(text) : undefined;
                outOfCoreStreamCadModelSettings.apparentSizeThreshold =
                  newValue;
              }}
            />
          </Stack>
        </Stack>
      )}
    </>
  );
}

function ShowPointerMovePreview(): JSX.Element {
  const {
    normalDetection,
    setNormalDetection,
    edgeCornerDetection,
    setEdgeCornerDetection,
  } = useFeatureDetectionContext();

  return (
    <>
      <FaroText variant="bodyM">Feature Detection</FaroText>
      <Stack direction="row" sx={{ alignItems: "center" }}>
        <FaroText variant="bodyS">Normal</FaroText>
        <Checkbox
          checked={normalDetection}
          onChange={(ev) => setNormalDetection(ev.target.checked)}
        />
        <FaroText variant="bodyS">Edge/Corner</FaroText>
        <Checkbox
          disabled={!normalDetection}
          checked={edgeCornerDetection}
          onChange={(ev) => setEdgeCornerDetection(ev.target.checked)}
        />
      </Stack>
    </>
  );
}

/**
 * @returns UI control that allows the user to log a complete iElement from the project, given its GUID.
 */
function LogElement(): JSX.Element {
  const [elementId, setElementId] = useState<string>("");
  const store = useAppStore();

  const logElement = useCallback(() => {
    if (!elementId) {
      console.log("Please enter an ID.");
      return;
    }

    const element = selectIElement(elementId)(store.getState());

    if (!element) {
      console.debug(
        "element not defined. Make sure that the ID is valid and that the corresponding part of the project is loaded.",
      );
      return;
    }

    console.log({ element });
  }, [elementId, store]);

  return (
    <Stack>
      <FaroText variant="bodyS">Log element</FaroText>
      <Stack direction="row" sx={{ alignItems: "center", gap: 1 }}>
        <TextField
          text={elementId}
          onTextChanged={(value) => setElementId(value)}
        />
        <FaroButton size="s" onClick={logElement}>
          Log it
        </FaroButton>
      </Stack>
    </Stack>
  );
}

function IElementsStatsCheckBox(): JSX.Element {
  const [iElementsStatsOn, setIElementsStatsOn] = useState(false);

  useIElementsStats(iElementsStatsOn);

  return (
    <Stack
      direction="row"
      sx={{
        width: "100%",
        alignItems: "center",
        justifyContent: "space-between",
      }}
    >
      <FaroText variant="bodyS">Project elements stats to console</FaroText>
      <Checkbox
        value={iElementsStatsOn}
        onChange={(ev) => setIElementsStatsOn(ev.target.checked)}
      />
    </Stack>
  );
}

function DebugTokenExpiration(): JSX.Element {
  const { tokenResetter } = useApiClientContext();

  return (
    <FaroButton
      size="s"
      onClick={() => {
        tokenResetter();
        console.log("Token reset successfully.");
      }}
    >
      Reset CoreAPI token
    </FaroButton>
  );
}
